import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, ICON_PLAYLIST_CHECK } from '@doveit/bricks';
import { Contact } from '../../../providers/api/dtos/contact';
import useContactLeads from '../../hooks/use-contact-leads/useContactLeads';
import { LeadStatus } from '../../../domain/types';

type LeadContext = 'leads' | 'cold-leads';

interface ChildFnProps {
  disabled: boolean,
  processLeads: VoidFunction,
}

export interface ProcessContactProps {
  contact: Contact,
  children?: (props: ChildFnProps) => React.ReactNode
}

const ProcessContact: React.FC<ProcessContactProps> = ({
  contact,
  children,
}) => {
  const navigate = useNavigate();
  const { data: leads, error: leadsError } = useContactLeads(contact.id!);

  const hasActiveHotLeads = React.useMemo(() => !!leads?.some(({ isCold, status }) => !isCold && status === LeadStatus.IN_PROGRESS), [leads]);
  const hasActiveColdLeads = React.useMemo(() => !!leads?.some(({ isCold, status }) => isCold && status === LeadStatus.IN_PROGRESS), [leads]);

  const goToProcessPage = React.useCallback((context: LeadContext) => {
    navigate(`/call-center/contacts/process/${contact.id}/${context}`);
  }, [navigate, contact]);

  const processLeads = React.useCallback(() => {
    if (hasActiveColdLeads && !hasActiveHotLeads) {
      goToProcessPage('cold-leads');
      return;
    }

    goToProcessPage('leads');
  }, [hasActiveColdLeads, hasActiveHotLeads, goToProcessPage]);

  const isDisabled = React.useMemo(
    () => !!(leadsError || leads?.length === 0 || !contact.phoneNumber),
    [leadsError, leads, contact],
  );

  if (children) {
    return children({
      disabled: isDisabled,
      processLeads,
    }) as React.ReactElement;
  }

  return (
    <ActionIcon
      icon={{ path: ICON_PLAYLIST_CHECK }}
      label="Processa lead"
      aria-label="Processa lead"
      disabled={isDisabled}
      onClick={processLeads}
    />
  );
};

export default ProcessContact;
